'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { ButtonGroupContext } from '../button-group/context/button-group.context';
import { Slot } from '../slot';
import { TextButtonRootContext } from './context/text-button-root.context';
import { textButtonRootRecipe } from './styles/text-button-root.styles';
import type { TextButtonRootPropsInterface } from './types/text-button-root.types';

/**
 * @since 10.0.0
 */

export const TextButtonRoot = forwardRef<HTMLButtonElement, TextButtonRootPropsInterface>(
  (props: TextButtonRootPropsInterface, forwardedRef) => {
    const groupContext = useContext(ButtonGroupContext);
    const isGrouped = Object.keys(groupContext).length > 0;
    const size = groupContext?.size || 'md';

    // Destructuring from `props` first so we can apply custom type to `textStyle`.
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      disabled = false,
      onImpact = false,
      textStyle = 'body-bold',
      type = 'button',
      ...rest
    } = props;

    const tone = onImpact && !props.tone ? 'neutral' : props.tone || 'brand';

    const styles = css(
      textButtonRootRecipe.raw({
        tone,
        disabled,
        onImpact,
        isGrouped,
        size,
      }),
      cssProp,
    );

    const Component = asChild ? Slot : 'button';

    // Real `<button>` uses the native `disabled` attribute; when rendered as an
    // anchor via `asChild`, fall back to aria-disabled + tabIndex + event blocking.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    const contextValue = useMemo(
      () => ({ tone, disabled, onImpact, size, textStyle }),
      [tone, disabled, onImpact, size, textStyle],
    );

    return (
      <TextButtonRootContext.Provider value={contextValue}>
        <Component
          ref={forwardedRef}
          data-c11n-component="TextButton.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_text-button__root', 'group', styles, className)}
          disabled={!asChild && disabled ? true : undefined}
          type={asChild ? undefined : type}
          {...rest}
          {...(asChild && disabled ? disabledInteractionsProps : {})}
        >
          {children}
        </Component>
      </TextButtonRootContext.Provider>
    );
  },
);

TextButtonRoot.displayName = 'TextButton.Root';
